// EP-156 T156-006 — tenant (Capacitor app/) FCM push.
// EP-157 T157-006 — the registration body also carries `app_id` (the native
// shell's bundle id), so the server can resolve a white-label app's own
// Firebase project per device (docs/ep-157-app-integration-push-credentials.md
// §3.2). The operator app and `POST /api/device_tokens` are untouched
// (decision 6) — this file only ever talks to `/api/customer/device_tokens`.
//
// Mirrors the operator app's web/src/lib/push/operatorPush.ts (EP-77), tenant
// side. Two things make this stricter than that file:
//
// - @capacitor-firebase/messaging and @capacitor/app are imported at the top,
//   the same way openPdf.ts imports @capacitor/filesystem and @capacitor/share.
//   They used to be dynamic imports so the LINE build would never fetch them;
//   Peerapat 1.0.4 build 11 never reached the iOS permission prompt and every
//   failure was swallowed, so the loading path was taken out of the question.
//   Both plugins only touch native code when a method is called, and every
//   method here sits behind isNativePlatform(), so the LINE build is unchanged.
// - `isNativePlatform()`/`getNativePlatform()` come from `@/lib/native`,
//   which reads `window.Capacitor` directly instead of importing
//   `@capacitor/core` — see that file's own comment. Using it here (instead
//   of `Capacitor.getPlatform()` the way operatorPush.ts does) keeps this
//   module free of any top-level Capacitor dependency too.
//
// Every exported function NO-OPS outside the native shell, and every FCM/
// network call is wrapped so a denied permission, a missing Firebase config
// (GoogleService-Info.plist / google-services.json not provisioned yet — see
// app/README.md), or an API hiccup can never break login, app open, or
// logout. Each failure is reported to Sentry, tagged with the step that
// failed, and nothing else (the scrubber drops any `token` key). `App.getInfo()` or `Device.getInfo()` failing is the same class of
// thing — the device still registers, just without `app_id` (falling back to
// the global FlowRent Firebase project server-side) or the versions.
import * as Sentry from '@sentry/react'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'
import { api } from '@/lib/api'
import i18n from '@/i18n'
import { isNativePlatform, getNativePlatform } from '@/lib/native'

type PushStep = 'permission' | 'token' | 'register' | 'rotate' | 'unregister' | 'deep_link' | 'resume'

function reportPushFailure(step: PushStep, error: unknown): void {
  Sentry.captureException(error, { tags: { area: 'tenant_push', push_step: step } })
}

function pushLocale(): 'en' | 'th' {
  return i18n.language?.startsWith('en') ? 'en' : 'th'
}

// Peerapat 1.0.4 (2026-10-08): on iOS, Messaging.token never answered (the
// APNs token was never handed to the plugin), so getToken() hung and logout,
// which awaited this cleanup, never finished. Logout's cleanup is abandoned
// after this long; registration is fire-and-forget and is not timed.
export const PUSH_CALL_TIMEOUT_MS = 4000

function withTimeout<T>(promise: Promise<T>, ms = PUSH_CALL_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('push call timed out')), ms)
    promise.then(
      (value) => { clearTimeout(timer); resolve(value) },
      (error) => { clearTimeout(timer); reject(error) },
    )
  })
}

interface NativeAppInfo {
  app_id?: string
  app_version?: string
  os_version?: string
}

/**
 * The native shell's bundle id and version (`App.getInfo()`) and the OS
 * version (`Device.getInfo()`, EP-160 T160-102l), for the support console's
 * tenant lookup. Each plugin is read on its own, so one failing leaves the
 * other's keys in place; a failure of both just registers the device without
 * them. Never throws.
 */
async function getNativeInfo(): Promise<NativeAppInfo> {
  const info: NativeAppInfo = {}
  try {
    const { id, version } = await App.getInfo()
    if (id) info.app_id = id
    if (version) info.app_version = version
  } catch {
    // keep going: the device still registers without these
  }
  try {
    const { osVersion } = await Device.getInfo()
    if (osVersion) info.os_version = osVersion
  } catch {
    // an older native build without the plugin lands here
  }
  return info
}

async function postToken(token: string): Promise<void> {
  const info = await getNativeInfo()
  await api.post('/api/customer/device_tokens', {
    token,
    platform: getNativePlatform(), // ios | android
    locale: pushLocale(),
    ...info,
  })
}

let rotationListenerAdded = false

// Owner 2026-10-09: the server sends an invoice notice to LINE instead when
// the app has not re-registered for 30 days (an uninstalled iPhone app can
// stay registered for days). So "re-registered" has to mean "used": a
// return to the foreground counts too, not only a fresh launch, but at most
// once per this interval so switching apps does not post every time.
export const PUSH_REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000
let lastRegisterAttemptAt = 0

/** Request permission, register the FCM token, and keep it fresh. Call after OTP login. */
export async function registerTenantPush(): Promise<void> {
  if (!isNativePlatform()) return
  lastRegisterAttemptAt = Date.now()
  let step: PushStep = 'permission'
  try {
    // Not timed: this waits for the person to answer the OS permission prompt.
    const perm = await FirebaseMessaging.requestPermissions()
    if (perm.receive !== 'granted') {
      // Not an error, but the one thing that tells "never asked" apart from
      // "said no" when a device does not register.
      Sentry.captureMessage('tenant push permission not granted', {
        level: 'info',
        tags: { area: 'tenant_push', push_step: 'permission', receive: perm.receive },
      })
      return
    }
    // Listen BEFORE asking for the token: on a first launch iOS and Firebase
    // can take a while to produce it, and a token that arrives late must
    // still reach the server.
    await addTokenRotationListener()
    // Not timed either: every caller fires this and forgets it, so a slow
    // first token never blocks anything (only logout's cleanup is timed).
    step = 'token'
    const { token } = await FirebaseMessaging.getToken()
    step = 'register'
    if (token) await postToken(token)
  } catch (error) {
    // best-effort: a denied permission or a missing Firebase config must never block login
    reportPushFailure(step, error)
  }
}

/** Delete the device's token server-side + locally. Call before clearing tokens on logout. */
export async function unregisterTenantPush(): Promise<void> {
  if (!isNativePlatform()) return
  try {
    // One limit for the whole cleanup, so logout waits at most this long.
    await withTimeout((async () => {
      const { token } = await FirebaseMessaging.getToken()
      if (token) await api.delete('/api/customer/device_tokens', { data: { token } })
      await FirebaseMessaging.deleteToken()
    })())
  } catch (error) {
    reportPushFailure('unregister', error) // best-effort
  }
}

async function addTokenRotationListener(): Promise<void> {
  if (rotationListenerAdded) return
  rotationListenerAdded = true
  await FirebaseMessaging.addListener('tokenReceived', async ({ token }) => {
    try {
      if (token) await postToken(token)
    } catch (error) {
      reportPushFailure('rotate', error) // best-effort
    }
  })
}

/**
 * Pure: extract the deep-link route from a tap event payload. Invoice pushes
 * (invoice_created / invoice_sent, the only two events a tenant device ever
 * receives today — EP-156 T156-004) carry `record_type`/`record_id`, not a
 * `route` key (PushNotifications.data_for only merges `route` for operator
 * events). A future `route` key is honoured first if one is ever added.
 */
export function routeFromNotification(event: unknown): string | null {
  const data = (event as { notification?: { data?: Record<string, unknown> } })?.notification?.data
  if (!data) return null

  const route = data.route
  if (typeof route === 'string' && route.startsWith('/')) return route

  if (data.record_type === 'Invoice') {
    const id = data.record_id
    if (typeof id === 'string' && /^\d+$/.test(id)) return `/invoices/${id}`
  }

  return null
}

/** Wire notification taps → React Router navigation. Call once inside the Router. */
export async function setupTenantPushDeepLinks(navigate: (path: string) => void): Promise<void> {
  if (!isNativePlatform()) return
  try {
    await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      const route = routeFromNotification(event)
      if (route) navigate(route)
    })
  } catch (error) {
    reportPushFailure('deep_link', error) // best-effort
  }
}

/**
 * Re-register when the app returns to the foreground (signed-in tenants
 * only; the caller removes the listener on logout). Returns the cleanup.
 */
export async function setupTenantPushRefreshOnResume(): Promise<() => void> {
  if (!isNativePlatform()) return () => {}
  try {
    const handle = await App.addListener('resume', () => {
      if (Date.now() - lastRegisterAttemptAt < PUSH_REFRESH_INTERVAL_MS) return
      void registerTenantPush()
    })
    return () => { void handle.remove() }
  } catch (error) {
    reportPushFailure('resume', error)
    return () => {}
  }
}

// Test seam — reset module state between specs.
export function __resetTenantPushForTests(): void {
  rotationListenerAdded = false
  lastRegisterAttemptAt = 0
}
